import {useEffect, useId, useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {ScopedCommands} from '@github-ui/ui-commands'
import {Button, Dialog, FormControl, IconButton, Stack, Text, TextInput} from '@primer/react'
import {ToolsIcon} from '@primer/octicons-react'
import {isValidSandboxImageId, useSandboxImageOverride} from '@github-ui/copilot-shared-ui/utils/sandbox-image-override'
import styles from './SandboxImageOverrideControl.module.css'

/** Staff-only wrench that sets the sandbox image new Cloud sessions boot with. Renders nothing for everyone else. */
export function SandboxImageOverrideControl({disabled = false}: {disabled?: boolean}) {
  const {canOverride, image, setImage} = useSandboxImageOverride()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  if (!canOverride) return null

  return (
    <div className={styles.Container}>
      {image ? (
        <Button
          ref={triggerRef}
          size="small"
          variant="invisible"
          leadingVisual={ToolsIcon}
          className={styles.ActiveTrigger}
          aria-label={`Image ${image.slice(0, 8)}. Sandbox image override active: ${image}. Edit or clear`}
          data-testid="sandbox-image-override-trigger"
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          Image {image.slice(0, 8)}
        </Button>
      ) : (
        <IconButton
          ref={triggerRef}
          size="small"
          variant="invisible"
          icon={ToolsIcon}
          aria-label="Set sandbox image override (staff only)"
          tooltipDirection="nw"
          className={styles.Trigger}
          data-testid="sandbox-image-override-trigger"
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        />
      )}
      {isOpen && (
        <SandboxImageOverrideDialog
          image={image}
          returnFocusRef={triggerRef}
          onSave={value => {
            setImage(value)
            setIsOpen(false)
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

function SandboxImageOverrideDialog({
  image,
  returnFocusRef,
  onSave,
  onClose,
}: {
  image: string | undefined
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  onSave: (image: string | undefined) => void
  onClose: () => void
}) {
  const [value, setValue] = useState(image ?? '')
  const [error, setError] = useState<{message: string} | undefined>()
  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const formId = useId()
  const trimmed = value.trim()
  const isValid = isValidSandboxImageId(trimmed)

  useEffect(() => {
    if (error && formRef.current) announce(error.message, {assertive: true, element: formRef.current})
  }, [error])

  function save(nextImage: string | undefined) {
    try {
      onSave(nextImage)
    } catch {
      setError({
        message: `Unable to ${nextImage ? 'save' : 'clear'} the image override. Check your browser storage permissions and available space, then try again.`,
      })
    }
  }

  return (
    <Dialog
      title="Sandbox image override"
      subtitle="Staff only. New Cloud sandbox sessions boot this image instead of the default."
      onClose={onClose}
      width="medium"
      returnFocusRef={returnFocusRef}
      initialFocusRef={inputRef}
      footerButtons={[
        {
          buttonType: 'danger',
          content: 'Clear override',
          disabled: !image,
          onClick: () => save(undefined),
        },
        {buttonType: 'default', content: 'Cancel', onClick: onClose},
        {buttonType: 'primary', content: 'Save', type: 'submit', form: formId},
      ]}
    >
      <ScopedCommands
        as="form"
        ref={formRef}
        id={formId}
        commands={{'github:submit-form': () => formRef.current?.requestSubmit()}}
        onSubmit={event => {
          event.preventDefault()
          if (!isValid) {
            setError({message: 'Enter a plain image UUID.'})
            return
          }
          save(trimmed)
        }}
      >
        <Stack direction="vertical" gap="normal">
          <FormControl>
            <FormControl.Label>Image ID</FormControl.Label>
            <TextInput
              ref={inputRef}
              block
              monospace
              value={value}
              autoComplete="off"
              spellCheck={false}
              onChange={event => {
                setValue(event.target.value)
                setError(undefined)
              }}
              validationStatus={error ? 'error' : undefined}
            />
            <FormControl.Caption>
              Enter a plain image UUID, for example 00000000-0000-0000-0000-000000000000. Saved in this browser.
              Pre-started sandboxes are discarded while an override is set.
            </FormControl.Caption>
            {error && <FormControl.Validation variant="error">{error.message}</FormControl.Validation>}
          </FormControl>
          <Text as="p" size="small" className={styles.Status} data-testid="sandbox-image-override-status">
            {image ? `Current override: ${image}` : 'No override set. Sessions use the default image.'}
          </Text>
        </Stack>
      </ScopedCommands>
    </Dialog>
  )
}
